"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Crop as CropIcon, Sun, Contrast, RotateCw, Check, X } from "lucide-react";

interface ImageEditorProps {
  /** Blob de l'image source */
  imageBlob: Blob;
  /** Callback avec l'image éditée */
  onSave: (editedBlob: Blob) => void;
  /** Callback annulation */
  onCancel: () => void;
}

/** Tourne une image de 90° CW et retourne un nouveau Blob */
async function rotateBlob90CW(blob: Blob): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      // Inverser largeur/hauteur pour 90° CW
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext("2d")!;
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (b) => resolve(b ?? blob),
        "image/jpeg",
        0.92
      );
    };
    img.src = url;
  });
}

/**
 * Éditeur d'image inline — rotation + crop + luminosité + contraste.
 * Conçu pour éditer des photos de plans (plans d'évacuation, etc.)
 */
export function ImageEditor({ imageBlob, onSave, onCancel }: ImageEditorProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [currentBlob, setCurrentBlob] = useState<Blob>(imageBlob);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [crop, setCrop] = useState<Crop>();
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);

  // Créer l'URL de l'image courante (mise à jour après rotation)
  useEffect(() => {
    const url = URL.createObjectURL(currentBlob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [currentBlob]);

  // Rotation 90° CW — génère un nouveau blob, reset le crop
  const handleRotate = useCallback(async () => {
    setRotating(true);
    try {
      const rotated = await rotateBlob90CW(currentBlob);
      setCurrentBlob(rotated);
      setCrop(undefined); // Reset le crop car les dimensions changent
    } finally {
      setRotating(false);
    }
  }, [currentBlob]);

  const handleSave = useCallback(async () => {
    if (!imgRef.current) return;
    setSaving(true);

    try {
      const img = imgRef.current;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      // Déterminer les dimensions de crop
      let sx = 0;
      let sy = 0;
      let sWidth = img.naturalWidth;
      let sHeight = img.naturalHeight;

      if (crop && crop.width && crop.height) {
        // Convertir les % en pixels
        sx = (crop.x / 100) * img.naturalWidth;
        sy = (crop.y / 100) * img.naturalHeight;
        sWidth = (crop.width / 100) * img.naturalWidth;
        sHeight = (crop.height / 100) * img.naturalHeight;
      }

      // Limiter la taille de sortie (max 3000px de large — netteté au zoom sur les plans)
      const maxWidth = 3000;
      let outWidth = sWidth;
      let outHeight = sHeight;
      if (outWidth > maxWidth) {
        outHeight = Math.round((outHeight * maxWidth) / outWidth);
        outWidth = maxWidth;
      }

      canvas.width = outWidth;
      canvas.height = outHeight;

      // Appliquer les filtres
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, outWidth, outHeight);

      // Exporter en JPEG
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(
          (b) => resolve(b ?? currentBlob),
          "image/jpeg",
          0.85
        );
      });

      onSave(blob);
    } finally {
      setSaving(false);
    }
  }, [crop, brightness, contrast, currentBlob, onSave]);

  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${contrast}%)`,
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Zone de crop */}
      <div className="overflow-hidden rounded-md border border-border bg-muted">
        {imageUrl && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            className="max-h-72"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Image à éditer"
              style={filterStyle}
              className="max-h-72 max-w-full"
              crossOrigin="anonymous"
            />
          </ReactCrop>
        )}
      </div>

      {/* Indication crop */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CropIcon className="size-3.5" />
        <span>Glissez sur l&apos;image pour recadrer</span>
      </div>

      {/* Réglages luminosité */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Sun className="size-4 text-muted-foreground" />
          <Label className="text-xs">Luminosité ({brightness}%)</Label>
        </div>
        <input
          type="range"
          min={30}
          max={200}
          value={brightness}
          onChange={(e) => setBrightness(Number(e.target.value))}
          className="w-full accent-primary"
        />
      </div>

      {/* Réglages contraste */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Contrast className="size-4 text-muted-foreground" />
          <Label className="text-xs">Contraste ({contrast}%)</Label>
        </div>
        <input
          type="range"
          min={30}
          max={200}
          value={contrast}
          onChange={(e) => setContrast(Number(e.target.value))}
          className="w-full accent-primary"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="tap-target"
          onClick={handleRotate}
          disabled={rotating}
          title="Tourner 90°"
        >
          <RotateCw className="size-4" />
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
        >
          <X className="size-4" />
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={saving}
        >
          <Check className="size-4" />
          {saving ? "..." : "Appliquer"}
        </Button>
      </div>
    </div>
  );
}
